import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MsalService } from '@azure/msal-angular';
import { TranslateService } from '@ngx-translate/core';
import { catchError, defaultIfEmpty, map, of } from 'rxjs';
import { ExtranetService } from '../services/extranet.service';
import { IdentityService } from '../services/identity.service';
import { MeResponse } from '../models/extranet.model';
import { isAuthBypassed } from '../auth/auth-bypass';

/** Where a member lands on a page their profiles do not open: Home is never NONE. */
export const ACCESS_DENIED_HOME = '/extranet/dashboard';

/** How long the "no access" toast stays (ms). */
export const ACCESS_DENIED_TOAST_MS = 4000;

/** Where a refused member is sent and the toast (an i18n key) they see there. */
export interface AccessDenial {
  redirectTo: string;
  toastKey: string;
}

/** The page is not open to the member's profiles: Home with "You don't have access to this page. Ask your group Owner." */
export const NO_ACCESS: AccessDenial = { redirectTo: ACCESS_DENIED_HOME, toastKey: 'profiles.noAccess' };

/**
 * What a gate decides for a manager: `admit`; `skip` — not this gate's business (cancelled with no
 * navigation and no toast: the role guard next to it redirects); or a denial.
 */
export type AccessVerdict = 'admit' | 'skip' | AccessDenial;

/**
 * The profile gate shared by `areaGuard` and `ownerGuard` (Feature 4123): always paired with a
 * role guard on the same route (`canActivate: [hotelManagerGuard, areaGuard('RATES')]`), which
 * keeps doing the sign-in and the role checks — this gate judges only what the member's profiles
 * open.
 *
 * - It resolves the identity fresh, like the role guards (`ExtranetService.getMe()` shares one
 *   request between the two guards of a navigation), and writes the answer into
 *   `IdentityService.me`, so the menu follows a permission change on the next navigation.
 * - A platform admin outside view-as is not restricted (decision D1); any role other than the two
 *   manager roles is the role guard's business: cancelled here, sent on by the role guard.
 * - A manager is judged by `judge(me, url)`: admitted, skipped (cancelled quietly, see
 *   `AccessVerdict`) or denied — sent to the denial's page with its toast, usually Home and
 *   "You don't have access to this page. Ask your group Owner." (`NO_ACCESS`; the text is read with
 *   `translate.get`, which waits for the language file on a first, deep-linked load).
 * - Signed out: cancelled — the role guard next to it starts the B2C sign-in (a second
 *   `loginRedirect` would only collide with the first). A failed lookup goes to /unauthorized and
 *   an empty one (re-authentication by redirect, WI 3861) cancels, as in the role guards.
 */
export function accessGate(judge: (me: MeResponse, url: string) => AccessVerdict): CanActivateFn {
  return (_route, state) => {
    const msal = inject(MsalService);
    const router = inject(Router);
    const extranetSvc = inject(ExtranetService);
    const identity = inject(IdentityService);
    const snackBar = inject(MatSnackBar);
    const translate = inject(TranslateService);

    if (!isAuthBypassed() && !msal.instance.getActiveAccount()) return false;

    return extranetSvc.getMe().pipe(
      map((me) => {
        identity.me.set(me);
        if (me.role === 'ADMIN') return true;
        if (me.role !== 'HOTEL_MANAGER' && me.role !== 'AGENCY_MANAGER') return false;
        const verdict = judge(me, state.url);
        if (verdict === 'admit') return true;
        if (verdict === 'skip') return false;
        router.navigate([verdict.redirectTo]);
        translate.get(verdict.toastKey).subscribe((text: string) =>
          snackBar.open(text, undefined, { duration: ACCESS_DENIED_TOAST_MS, panelClass: 'snack-error' }));
        return false;
      }),
      catchError(() => {
        router.navigate(['/unauthorized']);
        return of(false);
      }),
      defaultIfEmpty(false),
    );
  };
}
